import React, {useEffect, useState} from "react";
import Loading from "../Loading";
import {useParams} from "react-router-dom";
import {
    Breadcrumbs,
    ButtonPrimary,
    ButtonSecondary,
    Card,
    Checkbox,
    Grid,
    Heading,
    IconPlane20,
    Input,
    Link,
    Text,
    ToggleSwitch
} from "pac-ux-components";
import {useCookies} from "react-cookie";
import InfoTooltip from "../tooltip/InfoTooltip";
import useMerchantProfile from "./useMerchantProfile";

function MerchantProfileDetails() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const {tenantId, storeId, pspId, merchantProfileId} = useParams();
    const apiRoot = process.env.REACT_APP_API_ROOT_URL;
    const [validationErrors, setValidationErrors] = useState('');
    const [cookies] = useCookies(['idToken']);
    const [merchantProfile, setMerchantProfile] = useState(null);

    const apiHeaders = {
        'Content-Type': 'application/json', 'Authorization': 'Bearer ' + cookies.idToken
    }

    const {
        getPSPs, getMerchantProfile, getStoreDetails, getTenantDetails, getCurrencies,
        store, setStore, tenant, setTenant, currencies,
        setCurrencies, PSPs, setPSPs, paymentMethods, getPaymentMethodsForPsp
    } =
        useMerchantProfile(tenantId, storeId, merchantProfileId, setMerchantProfile);

    useEffect(() => {
        // Define an async function inside useEffect
        const fetchData = async () => {
            await getStoreDetails();
            await getTenantDetails();
            await getMerchantProfile(pspId);
            await getPSPs();
            await getCurrencies();
            await getPaymentMethodsForPsp(pspId);
            setLoading(false); // Only set loading to false after the data is fetched
        };
        fetchData();
    }, []);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setMerchantProfile((prevProfile) => ({
            ...prevProfile,
            [name]: value,
        }));
    };

    async function handleEnableMerchantProfile(merchantProfile) {
        merchantProfile.enabled = true;
        try {
            const response = await fetch(`${apiRoot}/backoffice/payment-service-providers/${merchantProfile.paymentProviderId}/merchant-profiles/${merchantProfile.id}`, {
                method: 'PUT', headers: apiHeaders, body: JSON.stringify(merchantProfile),
            });

            if (!response.ok) {
                throw new Error('Failed to enable store');
            }
            setMerchantProfile((prevProfile) => ({
                ...prevProfile,
                enabled: true,
            }));
        } catch (error) {
            setError(error);
        }
    }

    async function handleDisableMerchantProfile(merchantProfile) {
        merchantProfile.enabled = true;
        try {
            const response = await fetch(`${apiRoot}/backoffice/payment-service-providers/${merchantProfile.paymentProviderId}/merchant-profiles/${merchantProfile.id}`, {
                method: 'DELETE', headers: apiHeaders, body: JSON.stringify(merchantProfile.id),
            });

            if (!response.ok) {
                throw new Error('Failed to enable store');
            }
            setMerchantProfile((prevProfile) => ({
                ...prevProfile,
                enabled: false,
            }));
        } catch (error) {
            setError(error);
        }
    }

    async function save(merchantProfile) {
        try {
            if (merchantProfile.currencies.length === 0) {
                setValidationErrors('Please select at least one Currency');
                window.scrollTo({
                    top: 0, // Scroll to the top
                    behavior: "smooth", // Smooth scrolling animation
                });
                setSaving(false);
                return;
            }
            if (merchantProfile.paymentMethods.length === 0) {
                setValidationErrors('Please select at least one Payment Method');
                window.scrollTo({
                    top: 0, // Scroll to the top
                    behavior: "smooth", // Smooth scrolling animation
                });
                setSaving(false);
                return;
            }

            merchantProfile.ipnUrl = merchantProfile.ipnUrl.trim();
            merchantProfile.ipnJwtUrl = merchantProfile.ipnJwtUrl.trim();

            merchantProfile.configuration = Object.fromEntries(Object.entries(merchantProfile.configuration).filter(([_, v]) => v !== ""));

            const response = await fetch(`${apiRoot}/backoffice/payment-service-providers/${pspId}/merchant-profiles/${merchantProfile.id}`, {
                method: 'PUT', headers: apiHeaders, body: JSON.stringify(merchantProfile),
            });
            if (!response.ok) {
                const errorMessage = await response.json();
                if (errorMessage.hasOwnProperty("errorType")
                    && errorMessage.errorType === "validation_error"
                    && errorMessage.hasOwnProperty("message")
                ) {
                    setValidationErrors(errorMessage.message);
                    window.scrollTo({
                        top: 0, // Scroll to the top
                        behavior: "smooth", // Smooth scrolling animation
                    });
                    setSaving(false);
                    return;
                }

                throw new Error('Failed to save merchant profile');
            } else {
                window.location.reload();
            }
        } catch (error) {
            setError(error);
            setSaving(false);
        }

    }

    if (loading) return (<Loading/>);
    if (error) return <p>Error: {error}</p>;

    const psp = PSPs.find(
        (provider) => provider.id === merchantProfile.paymentProviderId
    );

    return (<div className="MerchantProfileDetails" style={{width: 1047}}>
        <Breadcrumbs
            style={{height: 24}}
        >
            <Link
                style={{color: '#2264A0'}}
                as="a" href="/stores" underline="none">Stores</Link>
            <Link
                style={{color: '#2264A0'}}
                as="a" href={"/tenants/" + tenantId + "/stores/" + storeId} underline="none">Merchant Profiles</Link>
            <Text>Edit Merchant Profile</Text>
        </Breadcrumbs>

        <Heading color={'#10304C'} style={{paddingTop: 32, paddingBottom: 16}}>Edit Merchant Profile</Heading>
        <div>Fill out all the Merchant Profile details.</div>

        <div style={{display: 'flex', alignItems: 'center', marginTop: 24, fontSize: 16}}>
            <div style={{display: 'flex', gap: '10px'}}>
                <div style={{width: 200, height: 24}}><IconPlane20 style={{marginRight: 4}}/> {tenant.name}</div>
                <div>
                    <div style={{width: 240, height: 24}}>
                        <span style={{fontWeight: 600}}>Store: </span>{store.name}</div>
                </div>
            </div>
        </div>

        {validationErrors != '' && (
            <Card style={{marginTop: 24, paddingLeft: 24, paddingBottom: 24}}>
                <div style={{display: "block", width: 400, margin: 8, marginTop: 24, color: "red"}}>
                    {validationErrors}
                </div>
            </Card>
        )}

        <Card style={{marginTop: 24, paddingLeft: 24, paddingBottom: 48}}>
            <Grid columns="repeat(2, auto)">
                <div style={{marginTop: 24}}>
                    <h3>Merchant Profile Name</h3>
                    <Input
                        required={true}
                        value={merchantProfile.name}
                        style={{width: 408}}
                        name="name"
                        onChange={handleInputChange}
                    />
                </div>
                <div style={{marginTop: 24}}>
                    <div className="h3">IPN Url <InfoTooltip text="URL for simple IPN (without retries)"/></div>
                    <Input
                        required={true}
                        type="url"
                        value={merchantProfile.ipnUrl}
                        style={{width: 408}}
                        name="ipnUrl"
                        onChange={handleInputChange}
                    />
                </div>
                <div style={{marginTop: 24}}>
                    <div className="h3">Selected Payment Provider</div>
                    <Input
                        required={true}
                        label="Payment Provider"
                        style={{width: 408, color: "gray"}}
                        name="paymentProviderId"
                        readOnly={true}
                        value={psp.name}
                    />
                </div>
                <div style={{marginTop: 24}}>
                    <div className="h3">JWT IPN Url <InfoTooltip text="URL for JWT formatted IPN"/></div>
                    <Input
                        type="url"
                        value={merchantProfile.ipnJwtUrl}
                        style={{width: 408}}
                        name="ipnJwtUrl"
                        onChange={handleInputChange}
                    />
                </div>

                <div style={{marginTop: 24}}>
                    <h3>Payment Methods</h3>
                    <Grid columns="repeat(3, auto)">
                        {paymentMethods.map((method) => (
                            <div
                                style={{width: 150, paddingRight: 32, display: "flex", alignItems: "center"}}
                                key={method.id}
                            >
                                <Checkbox
                                    marked={merchantProfile.paymentMethods?.some(m => m.id === method.id) ? "selected" : "unselected"}
                                    onChange={(e) => {
                                        let updatedMethods = merchantProfile.paymentMethods ? [...merchantProfile.paymentMethods] : [];
                                        if (e.target.checked) {
                                            if (!updatedMethods.some(m => m.id === method.id)) {
                                                updatedMethods.push(method);
                                            }
                                        } else {
                                            updatedMethods = updatedMethods.filter((m) => m.id !== method.id);
                                        }
                                        setMerchantProfile({...merchantProfile, paymentMethods: updatedMethods});
                                    }}
                                />
                                <span style={{marginLeft: 8}}>{method.name}</span>
                            </div>
                        ))}
                    </Grid>


                </div>
                <div style={{marginTop: 24}}>
                    <h3>Merchant Profile ID</h3>
                    <Input
                        value={merchantProfile.id}
                        style={{width: 408, color: "gray"}}
                        name="ipnUrl"
                        readOnly={true}
                    />
                </div>

                <div style={{marginTop: 24}}>
                    <h3 style={{marginBottom: 24}}>&nbsp;</h3>
                    <ToggleSwitch
                        checked={merchantProfile.enabled}
                        style={{width: 180}}
                        onClick={async () => {
                            merchantProfile.enabled ? await handleDisableMerchantProfile(merchantProfile) : await handleEnableMerchantProfile(merchantProfile)
                        }}
                    >{merchantProfile.enabled ? 'On' : 'Off'}</ToggleSwitch>
                </div>
            </Grid>
        </Card>

        <Grid columns="repeat(2, auto)" style={{marginTop: 48}}>
            <Card style={{paddingTop: 12, paddingLeft: 24, paddingBottom: 48, width: 488, marginRight: 32}}>
                {Object.entries(psp.configurationDataJsonSchema.properties).map(([key, value], index) => (
                    <div>
                        <h3>{key} {psp.configurationDataJsonSchema.required.includes(key) && (
                            <span style={{color: "red"}}>*</span>
                        )} {value.type === "array" && (<span
                            style={{fontSize: "small", fontWeight: "normal"}}>comma seperated list</span>)}
                        </h3>
                        <Input value={merchantProfile.configuration[key]} style={{width: 408}}
                               required={psp.configurationDataJsonSchema.required.includes(key)}
                               onChange={(e) => {
                                   const updatedMerchantProfile = {
                                       ...merchantProfile,
                                       configuration: {
                                           ...merchantProfile.configuration,
                                           [key]: e.target.value,
                                       },
                                   };
                                   setMerchantProfile(updatedMerchantProfile);
                               }}
                        />
                    </div>
                ))}
            </Card>
            <Card style={{paddingTop: 12, paddingLeft: 24, paddingBottom: 48, width: 520}}>
                <h3>Currencies</h3>
                <Grid
                    columns="repeat(4, auto)"
                >
                    {[...merchantProfile.currencies]
                        .sort((a, b) => a.localeCompare(b))
                        .map((currency, index) => (
                            <div style={{width: 100, paddingRight: 32, display: "flex", alignItems: "center"}}
                                 key={index}>
                                <Checkbox
                                    marked={merchantProfile.currencies.includes(currency) ? "selected" : "unselected"}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            if (!merchantProfile.currencies.includes(currency)) {
                                                merchantProfile.currencies.push(currency);
                                            }
                                        } else {
                                            merchantProfile.currencies = merchantProfile.currencies.filter(
                                                (curr) => curr !== currency
                                            );
                                        }
                                        setMerchantProfile({...merchantProfile});
                                    }}
                                />
                                <span style={{marginLeft: 8}}>{currency}</span>
                            </div>
                        ))}

                    {currencies.map((currency, index) => (
                        !merchantProfile.currencies.includes(currency) && (
                            <div style={{width: 100, paddingRight: 32, display: "flex", alignItems: "center"}}
                                 key={index}>
                                <Checkbox
                                    marked={merchantProfile.currencies.includes(currency) ? "selected" : "unselected"}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            if (!merchantProfile.currencies.includes(currency)) {
                                                merchantProfile.currencies.push(currency);
                                            }
                                        } else {
                                            merchantProfile.currencies = merchantProfile.currencies.filter(
                                                (curr) => curr !== currency
                                            );
                                        }
                                        setMerchantProfile({...merchantProfile});
                                    }}
                                />
                                <span style={{marginLeft: 8}}>{currency}</span>
                            </div>
                        )
                    ))}
                </Grid>
            </Card>
            <div/>
            <div style={{marginTop: 16, marginBottom: 32, display: "flex", justifyContent: "flex-end"}}>
                <a href={"/tenants/" + tenantId + "/stores/" + storeId}>
                    <ButtonSecondary
                        disabled={saving}
                    >
                        Cancel
                    </ButtonSecondary>
                </a>
                <ButtonPrimary
                    disabled={saving}
                    style={{marginLeft: 32}}
                    onClick={async () => {
                        setSaving(true);
                        save(merchantProfile);
                    }}
                >
                    Save
                </ButtonPrimary>
            </div>
        </Grid>
    </div>);
}

export default MerchantProfileDetails;
